import type { Static } from "@sinclair/typebox";
import type { VideoDetailType, VideoState } from "@volgo/core/contract";
import { useRef, useState } from "react";
import { Link } from "react-router";
import { useSession } from "../session";
import { Dialog } from "../ui/Dialog";
import { ErrorNote, Loading } from "../ui/States";
import { useLoad } from "../ui/useLoad";
import { Originals } from "./pages";
import { bytes, pending, useVisiblePoll, VideoCard, VideoStateNote, videoChanged } from "./shared";
import { finalizeForm, type VideoFormValue } from "./uploadQueue";
import { VideoFields } from "./VideoFields";

export function MinePage() {
  const { client } = useSession();
  const data = useLoad(() => client.call("videos.mine", { query: { limit: 80 } }), "mine");
  const [editing, setEditing] = useState<VideoDetailType | null>(null);
  const [removing, setRemoving] = useState<VideoDetailType | null>(null);
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const latest = useRef(data.data);
  latest.current = data.data;
  useVisiblePoll(!!data.data?.processing.count, async () => {
    if (!data.data) return;
    const items = await Promise.all(
      data.data.items.map(async (v) =>
        pending(v.state) ? client.call("videos.get", { params: { id: v.id } }) : v,
      ),
    );
    if (latest.current !== data.data) return;
    data.set({
      ...data.data,
      items,
      processing: { ...data.data.processing, count: items.filter((v) => pending(v.state)).length },
    });
  });
  return (
    <div className="page">
      <div className="page-head">
        <h1>올린 영상</h1>
        <Link className="btn btn-pri" to="/videos/upload">
          올리기
        </Link>
      </div>
      {data.data ? (
        <>
          <p>내 저장 용량 {bytes(data.data.storedBytes)}</p>
          <div className="video-grid">
            {data.data.items.map((v) => (
              <VideoCard key={v.id} video={v}>
                {v.state && !pending(v.state) && v.state.status !== "published" && (
                  <VideoStateNote state={v.state} />
                )}
                {v.state?.status === "failed" && (
                  <Link to="/videos/upload" state={{ retry: v }}>
                    다시 올리기
                  </Link>
                )}
                <div className="video-actions">
                  {v.state?.status !== "removed" && (
                    <button className="btn" type="button" onClick={() => setEditing(v)}>
                      편집
                    </button>
                  )}
                  <button className="btn" type="button" onClick={() => setRemoving(v)}>
                    삭제
                  </button>
                </div>
              </VideoCard>
            ))}
          </div>
          {data.data.nextCursor && (
            <button
              className="btn"
              disabled={busy}
              type="button"
              onClick={async () => {
                if (!data.data?.nextCursor) return;
                setBusy(true);
                try {
                  const next = await client.call("videos.mine", {
                    query: { cursor: data.data.nextCursor, limit: 80 },
                  });
                  data.set({ ...next, items: [...data.data.items, ...next.items] });
                } catch (e) {
                  setError(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              80개 더 보기
            </button>
          )}
          {data.data.items.length === 0 && <p>올린 영상이 없습니다.</p>}
        </>
      ) : data.status === "error" ? (
        <ErrorNote error={data.error} onRetry={data.reload} />
      ) : (
        <Loading />
      )}
      {editing && (
        <EditVideo
          video={editing}
          onClose={() => setEditing(null)}
          onSave={(v) => {
            if (data.data)
              data.set({ ...data.data, items: data.data.items.map((x) => (x.id === v.id ? v : x)) });
            setEditing(null);
            videoChanged();
          }}
        />
      )}
      {removing && (
        <Dialog label="영상 삭제" onClose={() => setRemoving(null)}>
          <h2>영상을 삭제할까요?</h2>
          <p>{removing.title} · 처리 중이면 처리도 멈춥니다.</p>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await client.call("videos.delete", { params: { id: removing.id } });
                if (data.data)
                  data.set({ ...data.data, items: data.data.items.filter((v) => v.id !== removing.id) });
                setRemoving(null);
                videoChanged();
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            삭제 확인
          </button>
          <button className="btn" type="button" onClick={() => setRemoving(null)}>
            취소
          </button>
        </Dialog>
      )}
      {error !== undefined && <ErrorNote error={error} />}
    </div>
  );
}
function EditVideo({
  video,
  onClose,
  onSave,
}: {
  video: VideoDetailType;
  onClose: () => void;
  onSave: (v: VideoDetailType) => void;
}) {
  const { client } = useSession();
  const [value, setValue] = useState<VideoFormValue>({
    title: video.title,
    description: video.description,
    tags: video.tags,
    seriesId: video.series?.id ?? null,
    episodeNo: video.series?.episodeNo ?? null,
    gids: video.links.map((g) => g.gid),
  });
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [valid, setValid] = useState(false);
  return (
    <Dialog label="영상 편집" onClose={onClose}>
      <form
        className="video-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            onSave(
              await client.call("videos.update", { params: { id: video.id }, body: finalizeForm(value) }),
            );
          } catch (e) {
            setError(e);
          } finally {
            setBusy(false);
          }
        }}
      >
        <h2>영상 편집</h2>
        <VideoFields value={value} onChange={setValue} onValidChange={setValid} />
        {error !== undefined && <ErrorNote error={error} />}
        <button className="btn btn-pri" type="submit" disabled={busy || !valid}>
          저장
        </button>
      </form>
    </Dialog>
  );
}
export function AdminVideosPage() {
  const { client } = useSession();
  const [status, setStatus] = useState<Static<typeof VideoState>["status"] | "">("");
  const [selected, setSelected] = useState<{ video: VideoDetailType; mode: "tags" | "remove" } | null>(null);
  const [input, setInput] = useState("");
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const data = useLoad(
    () => client.call("videos.adminList", { query: { status: status || undefined, limit: 80 } }),
    status,
  );
  const latest = useRef(data.data);
  latest.current = data.data;
  useVisiblePoll(true, async () => {
    if (busy || !data.data) return;
    const previous = data.data;
    const [summary, items] = await Promise.all([
      client.call("videos.adminList", { query: { status: status || undefined, limit: 1 } }),
      Promise.all(
        previous.items.map(async (v) =>
          pending(v.state) ? client.call("videos.get", { params: { id: v.id } }) : v,
        ),
      ),
    ]);
    if (latest.current !== previous) return;
    data.set({
      ...previous,
      items: items.filter((v) => !status || v.state?.status === status),
      server: summary.server,
      storedBytes: summary.storedBytes,
      users: summary.users,
    });
  });
  return (
    <section>
      <div className="page-head">
        <h1>영상 관리</h1>
        <select
          aria-label="영상 상태"
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
        >
          <option value="">모두</option>
          <option value="uploading">올리는 중</option>
          <option value="queued">대기</option>
          <option value="processing">처리 중</option>
          <option value="published">공개</option>
          <option value="failed">실패</option>
          <option value="removed">내려짐</option>
        </select>
      </div>
      {data.data ? (
        <>
          <div className="video-server">
            <h2>처리 서버</h2>
            {!data.data.server || Date.now() - Date.parse(data.data.server.lastReportAt) > 180000 ? (
              <p role="alert">처리 서버 응답이 3분 이상 없습니다.</p>
            ) : null}
            {data.data.server && (
              <p>
                마지막 응답 {new Date(data.data.server.lastReportAt).toLocaleString("ko-KR")} · 대기{" "}
                {data.data.server.queued} · 처리 중 {data.data.server.activeVideoId ?? "없음"} · 남은 공간{" "}
                {bytes(data.data.server.diskFreeBytes)}
              </p>
            )}
            <p>전체 저장 용량 {bytes(data.data.storedBytes)}</p>
            <details>
              <summary>사용자별 사용량</summary>
              {data.data.users.map((u) => (
                <p key={u.userId}>
                  {u.name}: {bytes(u.storedBytes)}
                </p>
              ))}
            </details>
          </div>
          <div className="video-grid">
            {data.data.items.map((v) => (
              <VideoCard key={v.id} video={v}>
                <p>
                  {v.videoCodec ?? "코덱 확인 전"} · 저장 {bytes(v.storedBytes ?? 0)}
                </p>
                {v.keyframeIntervalMs !== undefined && v.keyframeIntervalMs !== null && (
                  <p>
                    키프레임 간격 {(v.keyframeIntervalMs / 1000).toFixed(1)}초
                    {v.keyframeIntervalMs > 20000 && " · 탐색 간격이 깁니다"}
                  </p>
                )}
                <p>
                  {v.publishedAt ? new Date(v.publishedAt).toLocaleString("ko-KR") : "아직 공개되지 않음"}
                </p>
                <p>{v.tags.join(", ")}</p>
                {v.state && <VideoStateNote state={v.state} />}
                {v.policy.level !== "block" && <Originals links={v.links} />}
                <div className="video-actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setSelected({ video: v, mode: "tags" });
                      setInput(v.tags.join(", "));
                    }}
                  >
                    태그 수정
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={v.state?.status === "removed"}
                    onClick={() => {
                      setSelected({ video: v, mode: "remove" });
                      setInput("");
                    }}
                  >
                    내리기
                  </button>
                </div>
              </VideoCard>
            ))}
          </div>
          {data.data.nextCursor && (
            <button
              className="btn"
              type="button"
              disabled={busy}
              onClick={async () => {
                if (!data.data?.nextCursor) return;
                setBusy(true);
                try {
                  const next = await client.call("videos.adminList", {
                    query: { cursor: data.data.nextCursor, status: status || undefined, limit: 80 },
                  });
                  data.set({ ...next, items: [...data.data.items, ...next.items] });
                } catch (e) {
                  setError(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              80개 더 보기
            </button>
          )}
        </>
      ) : data.status === "error" ? (
        <ErrorNote error={data.error} />
      ) : (
        <Loading />
      )}
      {selected && (
        <Dialog
          label={selected.mode === "tags" ? "태그 수정" : "영상 내리기"}
          onClose={() => setSelected(null)}
        >
          <form
            className="video-form"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                if (selected.mode === "tags")
                  await client.call("videos.adminTags", {
                    params: { id: selected.video.id },
                    body: {
                      tags: [
                        ...new Set(
                          input
                            .split(",")
                            .map((t) => t.trim().toLowerCase())
                            .filter(Boolean),
                        ),
                      ],
                    },
                  });
                else
                  await client.call("videos.adminRemove", {
                    params: { id: selected.video.id },
                    body: { reason: input.trim() },
                  });
                setSelected(null);
                data.reload();
                videoChanged();
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              {selected.mode === "tags" ? "태그 (쉼표로 구분)" : "내리는 사유"}
              <textarea
                value={input}
                required={selected.mode === "remove"}
                maxLength={selected.mode === "remove" ? 1000 : undefined}
                onChange={(e) => setInput(e.target.value)}
              />
            </label>
            <button
              type="submit"
              className="btn btn-pri"
              disabled={busy || (selected.mode === "remove" && !input.trim())}
            >
              적용
            </button>
            {error !== undefined && <ErrorNote error={error} />}
          </form>
        </Dialog>
      )}
      {!selected && error !== undefined && <ErrorNote error={error} />}
    </section>
  );
}
